// roll-init.ts — dice rolling + initiative subsystem, carved out of template.js (Phase 2c-1 of
// plans/template-js-carve-up.md) and TS-ported for Phase 2a of plans/bundler-script-load-order-phase2.md —
// a member of the post-template-mid aggregate.
//
// MODULE-SCOPE SAFE: at eval only function defs + 6 window attaches; no eval-time DOM, no module-scope
// template.js reads. Deps (openSideLog (side-panels-init); queryData / queryDataInputs / ftDebugLog /
// setMyCampaignFastForwardWrapHidden / syncMyCampaignTimeSpinnerSlot (template.js); BASE_URL (core-helpers))
// are all click-time. rollComplexDice reads the page's bare die_*_dice / caster_id / roll_private / etc.
// named-element globals (unchanged by the move — esbuild keeps undeclared identifiers as global refs).
//
// PUBLIC GLOBALS (window-attached): rollDie, rollDice, rollComplexDice, rollInitiative, rollInitiativeFromSeed.
// (The creatures / players "sneak initiative" entries were retired 2026-09-11 — surprise is the stealth system's.)

import { startCombatPick } from './combat-pick';
import { type CombatProgress, refuseCombatChange } from '../shared/combat-settlement';
import { refuseUnported } from '../janus-engine/unported';

// RANDOM INT Between 1 and max
function rollDie(max: number): number {
    const rollResult = Math.floor(Math.random() * max) + 1;
    return rollResult;
}


async function rollDice(
    roll_type_1: unknown,
    roll_type_2: unknown,
    roll_item_id: unknown,
    roll_dc: unknown,
    dice: unknown,
    die: unknown,
    roll_mod: unknown,
    adv_dis_stacks: unknown,
): Promise<void> {

    // OPEN SIDE LOG
    openSideLog();

    const sl = getSidelogContext();
    if (!sl) return;

    // Lane ROLLS S1 (2026-09-27): the browser rolls (websocket bundle, roll-truth.ts → roll_truth_commit_inc.php kind `dice`). The legacy
    // roll_inc.php hand-back is DELETED (R-068, lane N4f 2026-10-03): an input the browser will not roll is refused here, with words,
    // before anything is posted (front end validates — the server only records). `roll_dc` was never read by the free roll.
    void roll_dc;
    const truth = (window as unknown as { ftTryCommitBareRoll?: (input: Record<string, unknown>, ctx: Record<string, unknown>) => 'handled' | null }).ftTryCommitBareRoll;
    const input = { roll_type_1, roll_type_2, roll_item_id, dice, die, roll_mod, adv_dis_stacks };
    if (typeof truth === 'function' && truth(input, sl) === 'handled') return;
    const warn = (window as unknown as { displayWarning?: (message: string) => void }).displayWarning;
    const message = typeof truth !== 'function'
        ? 'Dice cannot be rolled from this page. Reload and try again.'
        : 'That roll cannot be made: ' + bareRollRefusalReason(input);
    if (typeof warn === 'function') warn(message);
}

/** Why janus-engine/rolls/dice-roll.ts resolveBareRoll (+ roll-truth.ts tryCommitBareRoll) refused a free roll — the same limits, in
 *  their order, as words. */
function bareRollRefusalReason(input: Record<string, unknown>): string {
    const text = (v: unknown): string | null => v === null || v === undefined || typeof v === 'number' || typeof v === 'boolean' ? null : String(v);
    if (Object.values(input).some(v => { const s = text(v); return s !== null && (s !== s.trim() || /[%+<>\\\u0000]/.test(s)); })) {
        return 'use plain values (no spaces, plus signs or symbols).';
    }
    if (['random_table', 'random_loot_table', 'random_encounter_table', 'random_name_table'].includes(String(input.roll_type_1 ?? ''))) {
        return 'that table could not be identified.';
    }
    // roll_inc.php's grammar: a bracketed variable tag reads as 0; then the dice count and die size must be numbers.
    const field = (v: unknown): string => String(v ?? '').includes('[') ? '0' : String(v ?? '');
    const numeric = (v: string): boolean => /^[ \t\n\r\v\f]*[+-]?(?:\d+(?:\.\d*)?|\.\d+)(?:[eE][+-]?\d+)?[ \t\n\r\v\f]*$/.test(v);
    const diceS = field(input.dice), dieS = field(input.die);
    if (!numeric(diceS) || !numeric(dieS)) return 'the number of dice and the die size must be numbers.';
    const intval = (v: unknown): number => { const n = Math.trunc(parseFloat(String(v ?? ''))); return Number.isFinite(n) ? n : 0; };
    const dice = intval(diceS), die = intval(dieS), adv = intval(input.adv_dis_stacks), mod = intval(input.roll_mod);
    if (dice < 0 || die < 0) return 'the number of dice and the die size cannot be negative.';
    if (dice > 100) return 'at most 100 dice per roll.';
    if (die > 10000) return 'a die can have at most 10000 faces.';
    if (Math.abs(adv) > 10) return 'advantage / disadvantage is at most 10.';
    if (Math.abs(mod) > 100000) return 'the modifier must stay within ±100000.';
    // dice-roll.ts faceCount() > MAX_FACES (1000) — checked before roll-truth.ts's own item / label checks, in the engine's order
    if (dice > 0 && die > 0 && dice * (Math.abs(adv) + 1) > 1000) return 'one roll may draw at most 1000 dice, counting every advantage / disadvantage re-roll.';
    const itemId = String(input.roll_item_id ?? '');
    if (itemId !== '' && !/^[0-9A-F]{8}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{12}$/i.test(itemId.trim())) return 'the rolled item could not be identified.';
    if (String(input.roll_type_1 ?? '').length > 64 || String(input.roll_type_2 ?? '').length > 64) return 'the roll label is too long.';
    return 'the dice could not be rolled.';
}


async function rollComplexDice(): Promise<void> {
    // OPEN SIDE LOG
    openSideLog();

    // The #caster_id hidden input was removed (cache pivot 2026-06-22). The bare `caster_id` reference
    // threw a ReferenceError in this strict-mode bundle, and queryDataInputs reads payload values by
    // DOM element id so it could never see the cache-based caster anyway. Source caster from the cache
    // and POST literal values with queryData; the die_*/roll fields still have their hidden inputs, so
    // read them off the DOM by id.
    const getVal = (id: string): string =>
        (document.getElementById(id) as HTMLInputElement | null)?.value ?? '';
    const sl = getSidelogContext();
    if (!sl) return;

    // Lane ROLLS S1: the tray rolls in the browser (roll-truth.ts → roll_truth_commit_inc.php). The legacy
    // roll_complex_dice_inc.php hand-back is DELETED (R-069, lane N3 2026-10-01): an input the browser will not roll is
    // refused here, with words, before anything is posted (front end validates — the server only records).
    const truth = (window as unknown as { ftTryCommitComplexRoll?: (qty: Record<number, unknown>, mod: unknown, adv: unknown, ctx: Record<string, unknown>) => 'handled' | null }).ftTryCommitComplexRoll;
    const qty = { 4: getVal('die_4_dice'), 6: getVal('die_6_dice'), 8: getVal('die_8_dice'), 10: getVal('die_10_dice'), 12: getVal('die_12_dice'), 20: getVal('die_20_dice') };
    if (typeof truth === 'function' && truth(qty, getVal('raw_dice_mod'), getVal('raw_dice_adv_dis_stacks'), sl) === 'handled') return;
    const warn = (window as unknown as { displayWarning?: (message: string) => void }).displayWarning;
    const message = typeof truth !== 'function'
        ? 'Dice cannot be rolled from this page. Reload and try again.'
        : 'That roll cannot be made: ' + complexTrayRefusalReason(qty, getVal('raw_dice_mod'), getVal('raw_dice_adv_dis_stacks'));
    if (typeof warn === 'function') warn(message);
}

/** Why janus-engine/rolls/dice-roll.ts resolveComplexRoll refused the tray — the same limits, in its order, as words. */
function complexTrayRefusalReason(qty: Record<number, string>, mod: string, adv: string): string {
    const counts = Object.values(qty).map(v => v.trim());
    if ([...Object.values(qty), mod, adv].some(v => v !== v.trim() || /[%+<>\\\u0000]/.test(v))) return 'use plain numbers (no spaces, plus signs or symbols).';
    if (counts.some(v => v !== '' && !/^\d+$/.test(v))) return 'each die count must be a whole number of 0 or more.';
    if (counts.some(v => v !== '' && parseInt(v, 10) > 100)) return 'at most 100 of each die per roll.';
    // The resolver reads the modifier / advantage like PHP intval (a leading integer, else 0).
    const intval = (v: string): number => { const n = parseInt(v, 10); return Number.isFinite(n) ? n : 0; };
    const advN = intval(adv), modN = intval(mod);
    if (Math.abs(advN) > 10) return 'advantage / disadvantage is at most 10.';
    if (Math.abs(modN) > 100000) return 'the modifier must stay within ±100000.';
    return 'one roll may draw at most 1000 dice, counting every advantage / disadvantage re-roll.';
}


// Shared post-roll UI: hide the campaign-time controls now that combat runs the clock.
function afterInitiativeRolled(): void {
    (document.getElementById('play_campaign_time') as HTMLElement).style.display = 'none';
    (document.getElementById('pause_campaign_time') as HTMLElement).style.display = 'none';
    (document.getElementById('btn_in_combat_time_control') as HTMLElement).style.display = 'block';
    (document.getElementById('div_time_moving') as HTMLElement).style.display = 'none';
    document.querySelectorAll('.tr_campaign_time_control').forEach((element) => {
        (element as HTMLElement).style.display = 'none';
    });
    setMyCampaignFastForwardWrapHidden(true);
    syncMyCampaignTimeSpinnerSlot();
}

// The one combat start. seedIds = the aggressor the GM picked (owner 2026-08-26): combat grows from it by perception.
async function postRollInitiative(seedIds: string[], excludedIds: string[] = [], signal?: AbortSignal, progress?: CombatProgress): Promise<'handled' | null> {
    const sl = getSidelogContext();
    if (!sl) { refuseUnported('encounter-arr', { surface: 'initiative-pick' }, 'The encounter is still loading — try again in a moment.'); return null; }
    // The page decides combat; only the lane's handled outcome advances the UI. Gates, refusals and exceptions stop here.
    try {
        const entry = (window as unknown as { ftTryCommitStartCombat?: (input: { campaignId: string; encounterId: string; rollPrivate?: number; seedIds: string[]; excludedIds: string[]; signal?: AbortSignal; progress?: CombatProgress }) => Promise<'handled' | null> }).ftTryCommitStartCombat;
        if (typeof entry !== 'function') { refuseUnported('combat-lane-unavailable', { surface: 'initiative-pick' }, 'Combat controls are still loading — try again in a moment.'); return null; }
        if (await entry({ campaignId: String(sl.campaign_id), encounterId: String(sl.encounter_id), rollPrivate: Number(sl.roll_private ?? 2), seedIds, excludedIds, signal, progress }) !== 'handled') return null;
    } catch (error) { if (signal?.aborted) return null; return refuseCombatChange(error, { surface: 'initiative-pick', action: 'start' }); }
    afterInitiativeRolled();
    return 'handled';
}

// Roll Initiative arms "pick the aggressor" mode (combat-pick.ts): the GM clicks the creature that
// starts the fight, sees who joins and why, then confirms.
async function rollInitiative(): Promise<void> {
    ftDebugLog('combat', { phase: 'rollInitiative' });
    openSideLog();
    startCombatPick({ onStart: postRollInitiative });
}

async function addToCombat(): Promise<void> {
    const sl = getSidelogContext();
    if (!sl) return;
    const encounterId = String(sl.encounter_id);
    const encounter = (window as unknown as { FateTide?: { encounterCache?: Map<string, unknown> } }).FateTide?.encounterCache?.get(encounterId);
    const combatId = String(ftEncounterField(encounter, 'active_combat_id') ?? '').toUpperCase();
    if (!combatId) { showUserMessage('There is no active combat to join.'); return; }
    startCombatPick({ combatId, onStart: async (seedIds, excludedIds, signal, progress) => {
        const join = (window as unknown as { ftTryCommitJoinCombat?: (input: { campaignId: string; encounterId: string; combatId: string; seedIds: string[]; excludedIds: string[]; signal?: AbortSignal; progress?: CombatProgress }) => Promise<'handled' | null> }).ftTryCommitJoinCombat;
        if (!join) { refuseUnported('combat-lane-unavailable', { surface: 'initiative-pick' }, 'Combat controls are still loading — try again in a moment.'); return null; }
        return join({ campaignId: String(sl.campaign_id), encounterId, combatId, seedIds, excludedIds, signal, progress });
    } });
}


// Stealth v2 (owner 2026-08-27): the GM accepted the "Roll initiative from <spotter>?" prompt the
// log shows when a hidden creature is spotted. Skips the aggressor-pick phase — the spotter IS the
// seed — and opens the same roster review so the GM can add or remove participants.
async function rollInitiativeFromSeed(seedEncounterCreatureId: string): Promise<void> {
    const seed = String(seedEncounterCreatureId || '').trim();
    if (!seed) return;
    ftDebugLog('combat', { phase: 'rollInitiativeFromSeed', seed });
    openSideLog();
    startCombatPick({ initialSeedId: seed, onStart: postRollInitiative });
}

// ===========================================================================================
// Window attachment — the public surface (~24 PHP onclick sites + log/list-display/turn-widget bundles).
// ===========================================================================================
declare global {
    interface Window {
        rollDie: typeof rollDie;
        rollDice: typeof rollDice;
        rollComplexDice: typeof rollComplexDice;
        rollInitiative: typeof rollInitiative;
        addToCombat: typeof addToCombat;
        rollInitiativeFromSeed: typeof rollInitiativeFromSeed;
    }
}

window.rollDie = rollDie;
window.rollDice = rollDice;
window.rollComplexDice = rollComplexDice;
window.rollInitiative = rollInitiative;
window.addToCombat = addToCombat;
window.rollInitiativeFromSeed = rollInitiativeFromSeed;

